import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getProductNote, saveProductNote } from '../services/apiService';

function ProductTable({ products = [], onToggleSale, onOpenSettings, onOpenOrders, onOpenPriceStock, onOpenDetailSettings, onOpenSalesCondition, onNameClick, showOrdersButton = true, enableColumnFilters = false, nameLinkNeutral = false, dateOnly = false, singleDateFilter = false, disablePagination = false, showModalExtraColumns = false, actionsInName = false, pageSize = 20, onNoteSaved = null, hideLocationTypeColumns = false, showNoteBadgeColumns = false, settingsButtonLabel = '편집' }) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ field: '생성일시', dir: 'desc' });
  const [openActionsFor, setOpenActionsFor] = useState(null);
  const openActionsRootRef = useRef(null);
  const total = Array.isArray(products) ? products.length : 0;

  // Column filter states (only used when enableColumnFilters)
  const [nameQuery, setNameQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [saleFilter, setSaleFilter] = useState(''); // '', 'true', 'false'
  const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD (range mode)
  const [dateTo, setDateTo] = useState('');   // YYYY-MM-DD (range mode)
  const [dateExact, setDateExact] = useState(''); // YYYY-MM-DD (single day mode)
  // Extra column filters for modal view
  const [eventDateFrom, setEventDateFrom] = useState(''); // 최종행사일 (range)
  const [eventDateTo, setEventDateTo] = useState('');
  const [eventDateExact, setEventDateExact] = useState(''); // 최종행사일 (exact)
  const [noteQuery, setNoteQuery] = useState(''); // 상품비고
  const [badgeQuery, setBadgeQuery] = useState(''); // 상품뱃지

  // Note edit modal state
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteProduct, setNoteProduct] = useState(null);

  const openNoteModal = async (product) => {
    if (!product) return;
    setNoteProduct(product);
    setNoteText('');
    setNoteLoading(true);
    setNoteModalOpen(true);
    try {
      const fetched = await getProductNote({ productNo: product?.상품번호 || product?.상품코드 });
      const initial = fetched || product?.상품비고 || '';
      setNoteText(String(initial || ''));
    } catch (e) {
      console.error('Failed to load product note', e);
      setNoteText(String(product?.상품비고 || ''));
    } finally {
      setNoteLoading(false);
    }
  };

  const saveNote = async () => {
    if (!noteProduct) return;
    try {
      setNoteSaving(true);
      await saveProductNote({ productNo: noteProduct?.상품번호 || noteProduct?.상품코드, note: noteText });
      if (onNoteSaved) onNoteSaved(noteProduct, noteText);
      setNoteModalOpen(false);
      setNoteProduct(null);
      setNoteText('');
    } catch (e) {
      console.error('Failed to save product note', e);
      alert('상품비고 저장에 실패했습니다.');
    } finally {
      setNoteSaving(false);
    }
  };

  const filtered = useMemo(() => {
    if (!enableColumnFilters) return Array.isArray(products) ? products : [];
    const arr = Array.isArray(products) ? products : [];
    const norm = (s) => String(s || '').toLowerCase();
    const toDateMs = (row) => {
      const v = row?.['생성일시'] || row?.['등록일시'];
      const str = String(v || '').trim();
      const iso = str.includes('T') ? str : (str.length === 16 ? str.replace(' ', 'T') + ':00' : str.replace(' ', 'T'));
      const t = Date.parse(iso);
      return isNaN(t) ? 0 : t;
    };
    const toEventDateMs = (row) => {
      const v = row?.['최종행사일'];
      const str = String(v || '').trim();
      const iso = str.includes('T') ? str : (str.length === 16 ? str.replace(' ', 'T') + ':00' : str.replace(' ', 'T'));
      const t = Date.parse(iso);
      return isNaN(t) ? 0 : t;
    };
    const fromMs = !singleDateFilter && dateFrom ? Date.parse(`${dateFrom}T00:00:00`) : null;
    const toMs = !singleDateFilter && dateTo ? Date.parse(`${dateTo}T23:59:59`) : null;
    const exactStart = singleDateFilter && dateExact ? Date.parse(`${dateExact}T00:00:00`) : null;
    const exactEnd = singleDateFilter && dateExact ? Date.parse(`${dateExact}T23:59:59`) : null;
    const evFromMs = !singleDateFilter && eventDateFrom ? Date.parse(`${eventDateFrom}T00:00:00`) : null;
    const evToMs = !singleDateFilter && eventDateTo ? Date.parse(`${eventDateTo}T23:59:59`) : null;
    const evExactStart = singleDateFilter && eventDateExact ? Date.parse(`${eventDateExact}T00:00:00`) : null;
    const evExactEnd = singleDateFilter && eventDateExact ? Date.parse(`${eventDateExact}T23:59:59`) : null;

    return arr.filter((p) => {
      if (nameQuery && !norm(p.상품명).includes(norm(nameQuery))) return false;
      if (countryFilter && norm(p.국가) !== norm(countryFilter)) return false;
      if (cityFilter && norm(p.도시) !== norm(cityFilter)) return false;
      if (typeFilter && norm(p.상품타입) !== norm(typeFilter)) return false;
      if (saleFilter) {
        const want = saleFilter === 'true';
        if (Boolean(p.판매여부) !== want) return false;
      }
      if (singleDateFilter ? !!dateExact : (fromMs || toMs)) {
        const ms = toDateMs(p);
        if (singleDateFilter) {
          if (exactStart && ms < exactStart) return false;
          if (exactEnd && ms > exactEnd) return false;
        } else {
          if (fromMs && ms < fromMs) return false;
          if (toMs && ms > toMs) return false;
        }
      }
      // 최종행사일 필터
      if (singleDateFilter ? !!eventDateExact : (evFromMs || evToMs)) {
        const ms = toEventDateMs(p);
        if (singleDateFilter) {
          if (evExactStart && ms < evExactStart) return false;
          if (evExactEnd && ms > evExactEnd) return false;
        } else {
          if (evFromMs && ms < evFromMs) return false;
          if (evToMs && ms > evToMs) return false;
        }
      }
      // 상품비고 / 상품뱃지 텍스트 필터
      if (noteQuery && !norm(p.상품비고).includes(norm(noteQuery))) return false;
      if (badgeQuery && !norm(p.상품뱃지).includes(norm(badgeQuery))) return false;
      return true;
    });
  }, [enableColumnFilters, products, nameQuery, countryFilter, cityFilter, typeFilter, saleFilter, dateFrom, dateTo, singleDateFilter, dateExact, eventDateFrom, eventDateTo, eventDateExact, noteQuery, badgeQuery]);

  const optionSets = useMemo(() => {
    const arr = Array.isArray(products) ? products : [];
    const countries = new Set();
    const cities = new Set();
    const types = new Set();
    arr.forEach((p) => {
      if (p?.국가) countries.add(p.국가);
      if (!countryFilter || (p?.국가 && String(p.국가).toLowerCase() === String(countryFilter).toLowerCase())) {
        if (p?.도시) cities.add(p.도시);
      }
      if (p?.상품타입) types.add(p.상품타입);
    });
    return {
      countries: Array.from(countries).sort(),
      cities: Array.from(cities).sort(),
      types: Array.from(types).sort(),
    };
  }, [products, countryFilter]);

  const sorted = useMemo(() => {
    const arr = Array.isArray(filtered) ? [...filtered] : [];
    const { field, dir } = sort || {};
    if (!field) return arr;
    const getVal = (row) => {
      const v = row?.[field];
      if (field.includes('일시') || field.includes('일자') || field.toLowerCase().includes('date')) {
        const str = String(v || '').trim();
        const iso = str.includes('T') ? str : (str.length === 16 ? str.replace(' ', 'T') + ':00' : str.replace(' ', 'T'));
        const t = Date.parse(iso);
        return isNaN(t) ? 0 : t;
      }
      if (typeof v === 'number') return v;
      if (typeof v === 'boolean') return v ? 1 : 0;
      return String(v || '').toLowerCase();
    };
    arr.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sort]);

  const pageCount = disablePagination ? 1 : Math.max(1, Math.ceil(total / pageSize));
  const current = useMemo(() => {
    if (disablePagination) return sorted;
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize, disablePagination]);

  const onHeaderClick = (field) => {
    setPage(1);
    setSort((prev) => {
      if (!prev || prev.field !== field) return { field, dir: 'asc' };
      if (prev.dir === 'asc') return { field, dir: 'desc' };
      return { field: '', dir: 'asc' };
    });
  };

  const Th = ({ field, children, className = '' }) => {
    const active = sort.field === field;
    const arrow = active ? (sort.dir === 'asc' ? '▲' : '▼') : '';
    return (
      <th
        className={`px-3 py-2 text-center text-xs font-semibold text-gray-700 cursor-pointer select-none ${className}`}
        onClick={() => onHeaderClick(field)}
      >{children} {arrow}</th>
    );
  };

  const isNoEvents = (row) => {
    try {
      const raw = row?.['최종행사일'];
      if (!raw) return true;
      const s = String(raw).trim();
      const d = new Date(s.includes('T') ? s : `${s}T23:59:59`);
      if (isNaN(d.getTime())) return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d < today;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    if (!openActionsFor) return;

    const onDocPointer = (e) => {
      const root = openActionsRootRef.current;
      if (!root) {
        setOpenActionsFor(null);
        return;
      }
      if (root.contains(e.target)) return;
      setOpenActionsFor(null);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpenActionsFor(null);
    };

    document.addEventListener('mousedown', onDocPointer, true);
    document.addEventListener('touchstart', onDocPointer, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocPointer, true);
      document.removeEventListener('touchstart', onDocPointer, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [openActionsFor]);

  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-auto">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50/60">
            <tr>
              <Th field="상품명" className="w-[22rem]">상품명</Th>
              {!hideLocationTypeColumns && (
                <>
                  <Th field="국가">국가</Th>
                  <Th field="도시">도시</Th>
                  <Th field="상품타입">상품타입</Th>
                </>
              )}
              <Th field="노출여부">판매</Th>
              <Th field="생성일시">등록일</Th>
              {showModalExtraColumns && (
                <>
                  <Th field="최종행사일">최종행사일</Th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">상품비고</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">상품뱃지</th>
                </>
              )}
              {!showModalExtraColumns && showNoteBadgeColumns && (
                <>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">상품비고</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">상품뱃지</th>
                </>
              )}
              {!actionsInName && (
                <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">액션</th>
              )}
            </tr>
            {enableColumnFilters && (
              <tr className="align-top">
                <th className="px-3 py-2">
                  <input
                    type="text"
                    value={nameQuery}
                    onChange={(e) => { setPage(1); setNameQuery(e.target.value); }}
                    placeholder="상품명 검색"
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                </th>
                {!hideLocationTypeColumns && (
                  <>
                    <th className="px-3 py-2">
                      <select
                        className="w-full px-2 py-1 text-xs border rounded"
                        value={countryFilter}
                        onChange={(e) => { setPage(1); setCountryFilter(e.target.value); setCityFilter(''); }}
                      >
                        <option value="">전체</option>
                        {optionSets.countries.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </th>
                    <th className="px-3 py-2">
                      <select
                        className="w-full px-2 py-1 text-xs border rounded"
                        value={cityFilter}
                        onChange={(e) => { setPage(1); setCityFilter(e.target.value); }}
                      >
                        <option value="">전체</option>
                        {optionSets.cities.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </th>
                    <th className="px-3 py-2">
                      <select
                        className="w-full px-2 py-1 text-xs border rounded"
                        value={typeFilter}
                        onChange={(e) => { setPage(1); setTypeFilter(e.target.value); }}
                      >
                        <option value="">전체</option>
                        {optionSets.types.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </th>
                  </>
                )}
                <th className="px-3 py-2">
                  <select
                    className="w-full px-2 py-1 text-xs border rounded"
                    value={saleFilter}
                    onChange={(e) => { setPage(1); setSaleFilter(e.target.value); }}
                  >
                    <option value="">전체</option>
                    <option value="true">판매중</option>
                    <option value="false">중지</option>
                  </select>
                </th>
                <th className="px-3 py-2">
                  {singleDateFilter ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="date"
                        value={dateExact}
                        onChange={(e) => { setPage(1); setDateExact(e.target.value); }}
                        className="w-full px-2 py-1 text-xs border rounded"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
                        className="px-2 py-1 text-xs border rounded"
                      />
                      <span className="text-[10px] text-gray-400">~</span>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
                        className="px-2 py-1 text-xs border rounded"
                      />
                    </div>
                  )}
                </th>
                {showModalExtraColumns && (
                  <>
                    {/* 최종행사일 필터 */}
                    <th className="px-3 py-2">
                      {singleDateFilter ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="date"
                            value={eventDateExact}
                            onChange={(e) => { setPage(1); setEventDateExact(e.target.value); }}
                            className="w-full px-2 py-1 text-xs border rounded"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <input
                            type="date"
                            value={eventDateFrom}
                            onChange={(e) => { setPage(1); setEventDateFrom(e.target.value); }}
                            className="px-2 py-1 text-xs border rounded"
                          />
                          <span className="text-[10px] text-gray-400">~</span>
                          <input
                            type="date"
                            value={eventDateTo}
                            onChange={(e) => { setPage(1); setEventDateTo(e.target.value); }}
                            className="px-2 py-1 text-xs border rounded"
                          />
                        </div>
                      )}
                    </th>
                    {/* 상품비고 필터 */}
                    <th className="px-3 py-2">
                      <input
                        type="text"
                        value={noteQuery}
                        onChange={(e) => { setPage(1); setNoteQuery(e.target.value); }}
                        placeholder="상품비고 검색"
                        className="w-full px-2 py-1 text-xs border rounded"
                      />
                    </th>
                    {/* 상품뱃지 필터 */}
                    <th className="px-3 py-2">
                      <input
                        type="text"
                        value={badgeQuery}
                        onChange={(e) => { setPage(1); setBadgeQuery(e.target.value); }}
                        placeholder="상품뱃지 검색"
                        className="w-full px-2 py-1 text-xs border rounded"
                      />
                    </th>
                  </>
                )}
                {!showModalExtraColumns && showNoteBadgeColumns && (
                  <>
                    {/* 상품비고 필터 */}
                    <th className="px-3 py-2">
                      <input
                        type="text"
                        value={noteQuery}
                        onChange={(e) => { setPage(1); setNoteQuery(e.target.value); }}
                        placeholder="상품비고 검색"
                        className="w-full px-2 py-1 text-xs border rounded"
                      />
                    </th>
                    {/* 상품뱃지 필터 */}
                    <th className="px-3 py-2">
                      <input
                        type="text"
                        value={badgeQuery}
                        onChange={(e) => { setPage(1); setBadgeQuery(e.target.value); }}
                        placeholder="상품뱃지 검색"
                        className="w-full px-2 py-1 text-xs border rounded"
                      />
                    </th>
                  </>
                )}
                {!actionsInName && (
                  <th className="px-3 py-2">
                    <button
                      type="button"
                      className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-50"
                      onClick={() => { setPage(1); setNameQuery(''); setCountryFilter(''); setCityFilter(''); setTypeFilter(''); setSaleFilter(''); setDateFrom(''); setDateTo(''); setDateExact(''); setEventDateFrom(''); setEventDateTo(''); setEventDateExact(''); setNoteQuery(''); setBadgeQuery(''); }}
                    >초기화</button>
                  </th>
                )}
              </tr>
            )}
          </thead>
          <tbody className="divide-y divide-gray-100">
            {current.map((p) => {
              const rowKey = String(p?.상품번호 || p?.상품코드 || '');
              const menuOpen = !!rowKey && openActionsFor === rowKey;
              return (
              <tr key={p.상품번호 || p.상품코드} className="group hover:bg-blue-50/40">
                <td className="px-3 py-2 text-left text-sm text-gray-800">
                  <div className="flex items-start gap-2 justify-between">
                    <div className="flex flex-col min-w-0">
                      {onNameClick ? (
                        <button
                          type="button"
                          className={`font-medium text-left hover:underline ${isNoEvents(p) && showModalExtraColumns ? 'text-red-600' : (nameLinkNeutral ? 'text-gray-800' : 'text-grya-600')}`}
                          onClick={() => onNameClick(p)}
                          title="주문내역 보기"
                        >{p.상품명 || '-'}</button>
                      ) : (
                        <span className={`font-medium ${isNoEvents(p) && showModalExtraColumns ? 'text-red-600' : ''}`}>{p.상품명 || '-'}</span>
                      )}
                      <span className="text-[11px] text-gray-500">#{p.상품번호 || p.상품코드 || '-'}</span>
                    </div>
                    {actionsInName && (
                      <div
                        className="relative ml-2 shrink-0"
                        ref={menuOpen ? openActionsRootRef : undefined}
                        onClick={(e) => { try { e.stopPropagation(); } catch (_) {} }}
                      >
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-8 h-8 rounded border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                          aria-label="상품 액션 열기"
                          aria-haspopup="menu"
                          aria-expanded={menuOpen}
                          onClick={(e) => {
                            try { e.stopPropagation(); } catch (_) {}
                            if (!rowKey) return;
                            setOpenActionsFor((prev) => (prev === rowKey ? null : rowKey));
                          }}
                        >
                          <span className="text-lg leading-none">⋯</span>
                        </button>
                        {menuOpen && (
                          <div
                            className="absolute right-0 mt-1 w-36 rounded-md border border-gray-200 bg-white shadow-lg z-20 overflow-hidden"
                            role="menu"
                          >
                             <div className="divide-y divide-gray-100">
                              {showOrdersButton && onOpenOrders && (
                                <button
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenActionsFor(null);
                                    onOpenOrders(p);
                                  }}
                                >주문내역</button>
                              )}
                              {onOpenSalesCondition && (
                                <button
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenActionsFor(null);
                                    onOpenSalesCondition(p);
                                  }}
                                >판매조건 설정</button>
                              )}
                              {onOpenPriceStock && (
                                <button
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenActionsFor(null);
                                    onOpenPriceStock(p);
                                  }}
                                >가격/재고</button>
                              )}
                              {onOpenDetailSettings && (
                                <button
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenActionsFor(null);
                                    onOpenDetailSettings(p);
                                  }}
                                >상세설정</button>
                              )}
                              {onOpenSettings && (
                                <button
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenActionsFor(null);
                                    onOpenSettings(p);
                                  }}
                                >{settingsButtonLabel}</button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </td>
                {!hideLocationTypeColumns && (
                  <>
                    <td className="px-3 py-2 text-center text-sm text-gray-700">{p.국가 || '-'}</td>
                    <td className="px-3 py-2 text-center text-sm text-gray-700">{p.도시 || '-'}</td>
                    <td className="px-3 py-2 text-center text-sm text-gray-700">{p.상품타입 || '-'}</td>
                  </>
                )}
                <td className="px-3 py-2 text-center">
                  <button
                    type="button"
                    className={`inline-flex items-center px-2 py-1 text-xs rounded border ${p.판매여부 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                    onClick={() => onToggleSale && onToggleSale(p, !p.판매여부)}
                  >{p.판매여부 ? '판매중' : '중지'}</button>
                </td>
                <td className="px-3 py-2 text-center text-sm text-gray-700">{(() => {
                  const raw = p.생성일시 || p.등록일시 || '';
                  if (!raw) return '-';
                  if (!dateOnly) return raw;
                  const s = String(raw);
                  if (s.length >= 10) return s.slice(0, 10);
                  const t = Date.parse(s.includes('T') ? s : s.replace(' ', 'T'));
                  if (!isNaN(t)) {
                    const d = new Date(t);
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    return `${d.getFullYear()}-${mm}-${dd}`;
                  }
                  return s;
                })()}</td>
                {showModalExtraColumns && (
                  <>
                    <td className={`px-3 py-2 text-center text-sm ${isNoEvents(p) ? 'text-red-600' : 'text-gray-700'}`}>{(() => {
                      if (isNoEvents(p)) return '행사 없음';
                      const raw = p.최종행사일 || '';
                      if (!raw) return '행사 없음';
                      if (!dateOnly) return raw;
                      const s = String(raw);
                      if (s.length >= 10) return s.slice(0, 10);
                      const t = Date.parse(s.includes('T') ? s : s.replace(' ', 'T'));
                      if (!isNaN(t)) {
                        const d = new Date(t);
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const dd = String(d.getDate()).padStart(2, '0');
                        return `${d.getFullYear()}-${mm}-${dd}`;
                      }
                      return s;
                    })()}</td>
                    <td className="px-3 py-2 text-center text-sm text-gray-700 whitespace-pre-wrap break-words max-w-[24rem]">
                      <div className="group flex items-start gap-1">
                        <button
                          type="button"
                          className="text-center w-full cursor-pointer hover:underline decoration-dotted"
                          title="상품비고 편집"
                          aria-label="상품비고 편집"
                          onClick={() => openNoteModal(p)}
                        >{p.상품비고 || '-'}</button>
                        <svg className="mt-[2px] h-3.5 w-3.5 text-gray-400 opacity-80 group-hover:text-gray-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path d="M17.414 2.586a2 2 0 0 0-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 0 0 0-2.828z"/>
                          <path d="M5 15a1 1 0 0 0 1 1h9a1 1 0 1 1 0 2H6a3 3 0 0 1-3-3V6a1 1 0 1 1 2 0v9z"/>
                        </svg>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center text-sm text-gray-700 whitespace-pre-wrap break-words max-w-[16rem]">{p.상품뱃지 || '-'}</td>
                  </>
                )}
                {!showModalExtraColumns && showNoteBadgeColumns && (
                  <>
                    <td className="px-3 py-2 text-center text-sm text-gray-700 whitespace-pre-wrap break-words max-w-[24rem]">
                      <div className="group flex items-start gap-1">
                        <button
                          type="button"
                          className="text-center w-full cursor-pointer hover:underline decoration-dotted"
                          title="상품비고 편집"
                          aria-label="상품비고 편집"
                          onClick={() => openNoteModal(p)}
                        >{p.상품비고 || '-'}</button>
                        <svg className="mt-[2px] h-3.5 w-3.5 text-gray-400 opacity-80 group-hover:text-gray-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path d="M17.414 2.586a2 2 0 0 0-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 0 0 0-2.828z"/>
                          <path d="M5 15a1 1 0 0 0 1 1h9a1 1 0 1 1 0 2H6a3 3 0 0 1-3-3V6a1 1 0 1 1 2 0v9z"/>
                        </svg>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center text-sm text-gray-700 whitespace-pre-wrap break-words max-w-[16rem]">{p.상품뱃지 || '-'}</td>
                  </>
                )}
                {!actionsInName && (
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1.5 flex-wrap">
                      {showOrdersButton && (
                        <button
                          type="button"
                          className="px-2 py-1 text-[11px] rounded border bg-white hover:bg-gray-50"
                          onClick={() => onOpenOrders && onOpenOrders(p)}
                          title="주문내역"
                        >주문내역</button>
                      )}
                      <button
                        type="button"
                        className="px-2 py-1 text-[11px] rounded border bg-white hover:bg-gray-50"
                        onClick={() => onOpenPriceStock && onOpenPriceStock(p)}
                        title="가격/재고"
                      >가격/재고</button>
                      <button
                        type="button"
                        className="px-2 py-1 text-[11px] rounded border bg-white hover:bg-gray-50"
                        onClick={() => onOpenDetailSettings && onOpenDetailSettings(p)}
                        title="상세설정"
                      >상세설정</button>
                      {onOpenSettings && (
                        <button
                          type="button"
                          className="px-2 py-1 text-[11px] rounded bg-blue-600 text-white hover:bg-blue-700"
                          onClick={() => onOpenSettings(p)}
                          title="모달 편집"
                        >{settingsButtonLabel}</button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
            })}
            {current.length === 0 && (
              <tr>
                <td colSpan={(
                  // 기본 컬럼: 상품명, (국가,도시,상품타입), 판매, 등록일, (액션)
                  (hideLocationTypeColumns ? 3 : 6) + (actionsInName ? 0 : 1)
                ) + (showModalExtraColumns ? 3 : (showNoteBadgeColumns ? 2 : 0))} className="px-3 py-6 text-center text-sm text-gray-500">데이터가 없습니다.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!disablePagination && (
        <div className="flex items-center justify-between px-3 py-2 border-t bg-white">
          <div className="text-xs text-gray-600">총 {total}건 · {page}/{pageCount}페이지</div>
          <div className="inline-flex gap-1">
            <button
              type="button"
              className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >이전</button>
            <button
              type="button"
              className="px-2 py-1 text-xs rounded border bg-white hover:bg-gray-50"
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page === pageCount}
            >다음</button>
          </div>
        </div>
      )}
      {noteModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setNoteModalOpen(false)} />
          <div className="relative z-10 w-[min(720px,95vw)] max-h-[90vh] overflow-auto bg-white rounded-xl shadow-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-800">상품비고 편집</h3>
              <button type="button" className="px-2 py-1 text-sm rounded border bg-white hover:bg-gray-50" onClick={() => setNoteModalOpen(false)}>닫기</button>
            </div>
            <div className="mb-3 text-sm text-gray-600">
              <div><span className="text-gray-500">상품번호:</span> <span className="font-medium">{noteProduct?.상품번호 || noteProduct?.상품코드 || '-'}</span></div>
              <div className="truncate"><span className="text-gray-500">상품명:</span> <span className="font-medium">{noteProduct?.상품명 || '-'}</span></div>
            </div>
            <div>
              {noteLoading ? (
                <div className="py-10 text-center text-sm text-gray-500">불러오는 중...</div>
              ) : (
                <textarea
                  className="w-full h-64 border rounded-lg p-3 text-sm"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="상품비고를 입력하세요"
                />
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="px-3 py-2 text-sm rounded border bg-white hover:bg-gray-50" onClick={() => setNoteModalOpen(false)}>취소</button>
              <button type="button" className={`px-3 py-2 text-sm rounded ${noteSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} text-white`} onClick={saveNote} disabled={noteSaving}>{noteSaving ? '저장 중...' : '저장'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTable;
